<template>
  <v-content class="mt-0 pt-0">
  <v-container fluid>
    <pr-loading v-model="loading" />
    <v-snackbar v-model="showMessage" value="true" right top multi-line :color="msgType" :timeout="2000" >
      {{ msgText }}
      <v-btn dark flat @click="showMessage = false">
        {{ $t('OK') }}
      </v-btn>
    </v-snackbar>

    <v-toolbar flat color="white" class="elevation-1">
      <v-toolbar-title>{{ $t('Invoice.' + invoiceDocType) }} {{ invoiceDocNo }}</v-toolbar-title>
      <v-divider
        class="mx-2"
        inset
        vertical
      ></v-divider>

      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <v-btn icon slot="activator" @click="exportExcel" v-shortkey="['ctrl', 'alt', 'e']" @shortkey="exportExcel">
          <v-icon class="text--secondary">far fa-file-excel</v-icon>
        </v-btn>
        <span>{{ $t('Export to Excel') }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn icon slot="activator"  @click="exportPdf" v-shortkey="['ctrl', 'alt', 'p']" @shortkey="exportPdf" :loading="printing">
          <v-icon class="text--secondary">far fa-file-pdf</v-icon>
        </v-btn>
        <span>{{ $t('Export to PDF') }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn icon slot="activator" @click="$router.push({ name : 'invoices'})" v-shortkey="['ctrl', 'alt', 'b']" @shortkey="$router.push({ name : 'invoices'})">
          <v-icon class="text--secondary">arrow_back</v-icon>
        </v-btn>
        <span>{{ $t('Back to invoices list') }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-menu slot="activator"
          :close-on-content-click="false"
          :nudge-width="200"
          offset-x
        >
          <v-btn icon slot="activator">
            <v-icon class="text--secondary">settings</v-icon>
          </v-btn>

          <v-card>
            <v-list>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-title>{{ $t('Table settings') }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{ $t('Select visible columns') }}</v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>

            <v-divider></v-divider>

            <v-list>
              <v-list-tile v-for="(header,key) in headers" :key="key" v-if="header.value != 'actions'">
                <v-list-tile-action>
                  <v-switch v-model="header.show" color="purple" :disabled="columnHideCheck(header)" @change="storeFilter"></v-switch>
                </v-list-tile-action>
                <v-list-tile-title>{{ header.text }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-card>
        </v-menu>
        <span>{{ $t('Table settings') }}</span>
      </v-tooltip>
    </v-toolbar>

    <v-layout row wrap>
      <v-flex xs12>
        <v-data-table
          :headers="headers"
          :items="data"
          :pagination.sync="pagination"
          :total-items="totalItems"
          :loading="loading"
          :rows-per-page-items="[5, 10, 25]"
          :rows-per-page-text="$t('Rows per page')"
          class="elevation-1"
        >
          <template slot="headers" slot-scope="props">
            <tr>
              <th
                v-for="(header,idx) in props.headers"
                :key="idx"
                v-if="header.show"
                :class="['column sortable', '']"
              >
                <v-icon small>arrow_upward</v-icon>
                <v-tooltip v-if="header.tooltip" bottom>
                  <span slot="activator">
                    {{ header.text }}
                  </span>
                  <span>
                    {{ header.tooltip }}
                  </span>
                </v-tooltip>
                <span v-else>
                    {{ header.text }}
                </span>
              </th>
            </tr>
          </template>
          <template slot="items" slot-scope="props">
            <td v-for="(header,idx) in headers" :key="idx" v-if="props.item.type == 'shipment' && header.show && header.value !== 'actions'" :class="getColumnAlign(idx)">{{ getObjectValue(props.item, idx) }}</td>
            <td v-if="props.item.type != 'shipment'" :colspan="headers.length - 1">{{ props.item.description }}</td>
            <td v-if="props.item.type != 'shipment'" :class="getColumnAlign(headers.length - 1)">{{ getObjectValue(props.item, headers.length - 1) }}</td>
          </template>
        </v-data-table>
        <div class="text-xs-center pt-2">
          <v-pagination v-model="pagination.page" :length="pages" circle total-visible="7"></v-pagination>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
  </v-content>
</template>

<script>
import _object from "lodash/object";
import { invoice } from "../resources";
import { downloadFile, showDualAmount } from "../components/util";
import PrLoading from '@/components/Loading';
import { mapGetters, mapActions } from "vuex";

export default {
  components : {
    PrLoading
  },
  props : ['id'],
  data () {
    return {
      showMessage: false,
      msgType: 'info',
      msgText: '',

      invoiceDocType: '',
      invoiceDocNo: '',

      loading: false,
      printing: false,
      totalItems: 0,
      data: [],
      pagination: {},

      userFuncs: {},

      headers: [
        { text: this.$t('Date'),     value: 'date', type: 'date', align: 'left', sortable: false, show: true },
        { text: this.$t('Manifest'), value: 'manifest', align: 'center', sortable: false, show: true },
        { text: this.$t('Reference'), tooltip: this.$t('Reference.tooltip'), value: 'reference', align: 'center', sortable: false, show: true },
        { text: this.$t('Service Type'), value: 'service_type', type: 'enum', enum: 'ServiceType', align: 'center', sortable: false, show: true },
        { text: this.$t('Source Partner'),    value: 'source.partner.name', align: 'center', sortable: false, show: true },
        { text: this.$t('Source Settlement'), value: 'source.settlement.name', align: 'center', sortable: false, show: true },
        { text: this.$t('Target Partner'),    value: 'target.partner.name', align: 'center', sortable: false, show: true },
        { text: this.$t('Target Settlement'), value: 'target.settlement.name', align: 'center', sortable: false, show: true },
        { text: this.$t('Weight'), value : 'weight', align: 'right', sortable: false, show: true },
        { text: this.$t('Volume'), value : 'volume', align: 'right', sortable: false, show: true },
        { text: this.$t('Price'),  value : 'price', display: 'showDualAmount', align: 'right', sortable: false, show: true },
      ]
    }
  },

  computed : {
    ...mapGetters(['singleInvoiceFilter']),
    pages () {
      if (this.pagination.rowsPerPage == null ||
        this.totalItems == null
      ) return 0;

      return Math.ceil(this.totalItems / this.pagination.rowsPerPage);
    }
  },

  watch: {
    pagination: {
      handler () {
        this.listData();
      },
      deep: true
    }
  },

  created () {
    this.$set(this.userFuncs, 'showDualAmount', showDualAmount);
  },

  mounted () {
    this.invoiceDocType = 'INVOICE';
    this.invoiceDocNo = '#' + this.id;

    let storedFilter = null;
    try {
      storedFilter = JSON.parse(this.singleInvoiceFilter);
    } catch(e) {
      storedFilter = null;
    }

    if (storedFilter) {
      if (storedFilter.pagination)
        this.pagination = storedFilter.pagination;
      for (let i = 0; i < storedFilter.shown.length; i++)
        this.headers[i].show = storedFilter.shown[i];
    }

    this.listData();
  },

  methods: {
    ...mapActions(['updateSingleInvoiceFilter']),

    getObjectValue(obj, idx) {
      let v = _object.get(obj, this.headers[idx].value);
      let d = this.headers[idx].display;
      let t = this.headers[idx].type;
      if (d) {
        if (this.userFuncs[d])
          return this.userFuncs[d](v);
        else
          console.log("Display function " + d + " not found");
      }

      if (t === 'date')
        return v ? new Date(v).toLocaleDateString() : '';
      if (t === 'enum' && v) {
        let e = this.headers[idx].enum;
        if (!e)
          e = "Enum";
        return this.$t(e + '.' + v);
      }
      if (typeof v === 'boolean')
        return this.$t('Boolean.' + v);
      if (typeof v === 'number')
        return v.toLocaleString();
      return v;
    },

    getDataFromApi () {
      if (this.loading)
        return new Promise((resolve, reject) => reject());

      return new Promise((resolve, reject) => {
        const { sortBy, descending, page, rowsPerPage } = this.pagination;

        this.loading = true;
        let id = this.id;
        let offset = (page>=1 && rowsPerPage>=1) ? (page - 1) * rowsPerPage : 0;
        let length = (rowsPerPage>=1) ? rowsPerPage : -1;

        invoice.load({ id, offset, length }).then(response => {
          const doctype = response.body.type;
          const number = response.body.number;
          const items = response.body.lines;
          const total = response.body.length;
          resolve({
            items,
            total,
            doctype,
            number
          });
        }, response => {
          console.log("Error loading invoices " + response.status);
        }).finally(() => {
          this.loading = false;
        });
      });
    },

    listData() {
      this.storeFilter();
      this.getDataFromApi().then(data => {
        this.data = data.items;
        this.totalItems = data.total;
        this.invoiceDocNo = data.number;
        this.invoiceDocType = data.doctype;
      }, () => {
        console.log("Loading rejected");
      });
    },

    columnHideCheck(header) {
      if (!header.show)
        return false;

      let v = 0;
      for (let i = 0; i < this.headers.length; i++)
        if (this.headers[i].value != 'actions' && this.headers[i].show)
          v++;

      return (v<2);
    },

    getColumnAlign(idx) {
      if (this.headers[idx].align)
        return "text-xs-" + this.headers[idx].align;
      return "text-xs-left";
    },

    storeFilter() {
      let a = new Array();
      for (let i = 0; i < this.headers.length; i++)
        if (this.headers[i].value !== 'actions')
          a.push(this.headers[i].show);

      let s = {
        pagination : this.pagination,
        shown      : a
      };
      this.updateSingleInvoiceFilter(JSON.stringify(s));
    },

    exportPdf() {
      if (this.loading)
        return;

      if (!this.printing)
        this.loading = true;

      invoice.printPdf({ id : this.id }).then(response => {
        if (response.status === 200) {
          this.printing = false;
          downloadFile(response);
        }
        else if (response.status === 202) {
          this.msg(this.$t("Invoice PDF print requested"), "info");
          this.printing = true;
          setTimeout(() => this.exportPdf(), 3000);
        }
        else if (response.status === 204) {
          this.printing = true;
          setTimeout(() => this.exportPdf(), 3000);
        }

      }).catch(error => {
        this.msg(error.statusText);
      }).finally(
        () => this.loading = false
      );
    },

    exportExcel() {
      if (this.loading)
        return;

      this.loading = true;
      invoice.loadXls({ id : this.id }).then(response => {
        downloadFile(response);
      }, error => {
        this.msg(error.statusText);
      }).finally(() => {
        this.loading = false;
      });
    },

    msg(text, type) {
      this.msgText = text;
      this.msgType = (type) ? type : "error";
      this.showMessage = true;
    }
  }
}
</script>

<style scoped>
</style>